import React, { useState, useEffect } from 'react';
import SMSF_RATES_CONFIG from './SMSF_Rates_Config.js';

const SMSFEnhancedFeeAnalysis = ({ smsfState, setSmsfState }) => {
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [benchmarkData, setBenchmarkData] = useState({});
    const [feeOptimization, setFeeOptimization] = useState({});

    // Industry benchmark data (simplified)
    const industryBenchmarks = {
        smsf: {
            name: 'SMSF Average',
            totalFees: 4200,
            auditFees: 1500,
            adminFees: 2000,
            accountingFees: 700,
            description: 'Industry average for SMSF with $1M balance'
        },
        industry: {
            name: 'Industry Super',
            totalFees: 1800,
            auditFees: 0,
            adminFees: 1800,
            accountingFees: 0,
            description: 'Industry super fund fees'
        },
        retail: {
            name: 'Retail Super',
            totalFees: 3200,
            auditFees: 0,
            adminFees: 2500,
            accountingFees: 700,
            description: 'Retail super fund fees'
        },
        wrap: {
            name: 'Wrap Platform',
            totalFees: 4800,
            auditFees: 0,
            adminFees: 2800,
            accountingFees: 2000,
            description: 'Wrap platform fees'
        }
    };

    // Calculate current SMSF fees
    const calculateCurrentFees = () => {
        const auditFees = smsfState.smsfAuditFees || 0;
        const adminFees = smsfState.smsfAdminFees || 0;
        const accountingFees = smsfState.smsfAccountingFees || 0;
        const totalFees = auditFees + adminFees + accountingFees;
        
        // Estimate balance for percentage calculation (simplified)
        const estimatedBalance = 1000000; // Default $1M for comparison
        const feesAsPercentage = (totalFees / estimatedBalance) * 100;
        
        return {
            auditFees,
            adminFees,
            accountingFees,
            totalFees,
            feesAsPercentage,
            estimatedBalance
        };
    };

    const currentFees = calculateCurrentFees();

    // Fee optimization recommendations
    const getFeeOptimizationRecommendations = () => {
        const recommendations = [];
        
        // Audit fee optimization
        if (currentFees.auditFees > 1800) {
            recommendations.push({
                category: 'Audit Fees',
                current: currentFees.auditFees,
                recommended: 1500,
                savings: currentFees.auditFees - 1500,
                action: 'Consider competitive audit quotes or group audit arrangements',
                priority: 'High'
            });
        }
        
        // Admin fee optimization
        if (currentFees.adminFees > 2200) {
            recommendations.push({
                category: 'Administration Fees',
                current: currentFees.adminFees,
                recommended: 1800,
                savings: currentFees.adminFees - 1800,
                action: 'Review administration services and consider alternatives',
                priority: 'Medium'
            });
        }
        
        // Accounting fee optimization
        if (currentFees.accountingFees > 800) {
            recommendations.push({
                category: 'Accounting Fees',
                current: currentFees.accountingFees,
                recommended: 600,
                savings: currentFees.accountingFees - 600,
                action: 'Optimize record keeping and consider automation',
                priority: 'Medium'
            });
        }
        
        return recommendations;
    };

    const optimizationRecommendations = getFeeOptimizationRecommendations();

    // Break-even analysis
    const calculateBreakEvenAnalysis = () => {
        const smsfTotalFees = currentFees.totalFees;
        const industryTotalFees = industryBenchmarks.industry.totalFees;
        const retailTotalFees = industryBenchmarks.retail.totalFees;
        
        const smsfSavingsVsIndustry = smsfTotalFees - industryTotalFees;
        const smsfSavingsVsRetail = smsfTotalFees - retailTotalFees;
        
        // Calculate break-even balance (where SMSF becomes cost-effective)
        const breakEvenVsIndustry = (smsfSavingsVsIndustry / 0.0024) * 100; // 0.24% average difference
        const breakEvenVsRetail = (smsfSavingsVsRetail / 0.0014) * 100; // 0.14% average difference
        
        return {
            smsfSavingsVsIndustry,
            smsfSavingsVsRetail,
            breakEvenVsIndustry,
            breakEvenVsRetail,
            isSmsfCostEffective: smsfTotalFees < industryTotalFees
        };
    };

    const breakEvenAnalysis = calculateBreakEvenAnalysis();

    // Historical fee tracking (mock data)
    const historicalFees = [
        { year: '2021', totalFees: 3800, auditFees: 1400, adminFees: 1800, accountingFees: 600 },
        { year: '2022', totalFees: 4000, auditFees: 1450, adminFees: 1900, accountingFees: 650 },
        { year: '2023', totalFees: 4200, auditFees: 1500, adminFees: 2000, accountingFees: 700 },
        { year: '2024', totalFees: currentFees.totalFees, auditFees: currentFees.auditFees, adminFees: currentFees.adminFees, accountingFees: currentFees.accountingFees }
    ];

    return (
        <div className="space-y-6 p-6 bg-gray-50 rounded-lg shadow-inner">
            <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-800">Enhanced Fee Analysis</h2>
                <button
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                    {showAdvanced ? 'Hide' : 'Show'} Advanced Analysis
                </button>
            </div>

            {/* Current Fee Summary */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Current Fee Summary</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                        <div className="text-2xl font-bold text-blue-600">
                            ${currentFees.totalFees.toLocaleString()}
                        </div>
                        <div className="text-sm text-blue-700">Total Annual Fees</div>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                        <div className="text-2xl font-bold text-green-600">
                            {currentFees.feesAsPercentage.toFixed(3)}%
                        </div>
                        <div className="text-sm text-green-700">As % of Balance</div>
                    </div>
                    <div className="text-center p-4 bg-purple-50 rounded-lg">
                        <div className="text-2xl font-bold text-purple-600">
                            ${currentFees.auditFees.toLocaleString()}
                        </div>
                        <div className="text-sm text-purple-700">Audit Fees</div>
                    </div>
                    <div className="text-center p-4 bg-orange-50 rounded-lg">
                        <div className="text-2xl font-bold text-orange-600">
                            ${currentFees.adminFees.toLocaleString()}
                        </div>
                        <div className="text-sm text-orange-700">Admin Fees</div>
                    </div>
                </div>
            </div>

            {/* Industry Benchmarking */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Industry Benchmarking</h3>
                <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-gray-300">
                        <thead>
                            <tr className="bg-gray-50">
                                <th className="border border-gray-300 px-4 py-2 text-left">Fund Type</th>
                                <th className="border border-gray-300 px-4 py-2 text-right">Total Fees</th>
                                <th className="border border-gray-300 px-4 py-2 text-right">Audit</th>
                                <th className="border border-gray-300 px-4 py-2 text-right">Admin</th>
                                <th className="border border-gray-300 px-4 py-2 text-right">Accounting</th>
                                <th className="border border-gray-300 px-4 py-2 text-right">vs SMSF</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Object.entries(industryBenchmarks).map(([key, benchmark]) => {
                                const difference = benchmark.totalFees - currentFees.totalFees;
                                const isBetter = difference > 0;
                                return (
                                    <tr key={key} className={key === 'smsf' ? 'bg-blue-50' : ''}>
                                        <td className="border border-gray-300 px-4 py-2 font-medium">
                                            {benchmark.name}
                                            {key === 'smsf' && <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Your SMSF</span>}
                                        </td>
                                        <td className="border border-gray-300 px-4 py-2 text-right">${benchmark.totalFees.toLocaleString()}</td>
                                        <td className="border border-gray-300 px-4 py-2 text-right">${benchmark.auditFees.toLocaleString()}</td>
                                        <td className="border border-gray-300 px-4 py-2 text-right">${benchmark.adminFees.toLocaleString()}</td>
                                        <td className="border border-gray-300 px-4 py-2 text-right">${benchmark.accountingFees.toLocaleString()}</td>
                                        <td className={`border border-gray-300 px-4 py-2 text-right ${isBetter ? 'text-green-600' : 'text-red-600'}`}>
                                            {isBetter ? '+' : ''}${difference.toLocaleString()}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Fee Optimization Recommendations */}
            {optimizationRecommendations.length > 0 && (
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <h3 className="text-lg font-semibold mb-4">Fee Optimization Recommendations</h3>
                    <div className="space-y-4">
                        {optimizationRecommendations.map((rec, index) => (
                            <div key={index} className={`p-4 border-l-4 rounded-lg ${
                                rec.priority === 'High' ? 'border-red-500 bg-red-50' :
                                rec.priority === 'Medium' ? 'border-yellow-500 bg-yellow-50' :
                                'border-green-500 bg-green-50'
                            }`}>
                                <div className="flex items-center justify-between mb-2">
                                    <h4 className="font-semibold">{rec.category}</h4>
                                    <span className={`px-2 py-1 text-xs rounded ${
                                        rec.priority === 'High' ? 'bg-red-100 text-red-800' :
                                        rec.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                                        'bg-green-100 text-green-800'
                                    }`}>
                                        {rec.priority} Priority
                                    </span>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                                    <div>
                                        <span className="text-gray-600">Current:</span>
                                        <span className="ml-2 font-medium">${rec.current.toLocaleString()}</span>
                                    </div>
                                    <div>
                                        <span className="text-gray-600">Recommended:</span>
                                        <span className="ml-2 font-medium">${rec.recommended.toLocaleString()}</span>
                                    </div>
                                    <div>
                                        <span className="text-gray-600">Potential Savings:</span>
                                        <span className="ml-2 font-medium text-green-600">${rec.savings.toLocaleString()}</span>
                                    </div>
                                </div>
                                <p className="text-sm text-gray-600 mt-2">{rec.action}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Break-Even Analysis */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Break-Even Analysis</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                        <h4 className="font-semibold text-gray-700">SMSF vs Industry Super</h4>
                        <div className="space-y-2">
                            <div className="flex justify-between">
                                <span>Fee Difference:</span>
                                <span className={breakEvenAnalysis.smsfSavingsVsIndustry > 0 ? 'text-red-600' : 'text-green-600'}>
                                    ${Math.abs(breakEvenAnalysis.smsfSavingsVsIndustry).toLocaleString()}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span>Break-Even Balance:</span>
                                <span className="font-medium">
                                    ${breakEvenAnalysis.breakEvenVsIndustry.toLocaleString()}
                                </span>
                            </div>
                            <div className={`p-2 rounded text-sm ${
                                breakEvenAnalysis.smsfSavingsVsIndustry > 0 ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
                            }`}>
                                {breakEvenAnalysis.smsfSavingsVsIndustry > 0 
                                    ? 'SMSF is more expensive than industry super'
                                    : 'SMSF is cost-effective compared to industry super'
                                }
                            </div>
                        </div>
                    </div>
                    <div className="space-y-4">
                        <h4 className="font-semibold text-gray-700">SMSF vs Retail Super</h4>
                        <div className="space-y-2">
                            <div className="flex justify-between">
                                <span>Fee Difference:</span>
                                <span className={breakEvenAnalysis.smsfSavingsVsRetail > 0 ? 'text-red-600' : 'text-green-600'}>
                                    ${Math.abs(breakEvenAnalysis.smsfSavingsVsRetail).toLocaleString()}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span>Break-Even Balance:</span>
                                <span className="font-medium">
                                    ${breakEvenAnalysis.breakEvenVsRetail.toLocaleString()}
                                </span>
                            </div>
                            <div className={`p-2 rounded text-sm ${
                                breakEvenAnalysis.smsfSavingsVsRetail > 0 ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
                            }`}>
                                {breakEvenAnalysis.smsfSavingsVsRetail > 0 
                                    ? 'SMSF is more expensive than retail super'
                                    : 'SMSF is cost-effective compared to retail super'
                                }
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Advanced Analysis */}
            {showAdvanced && (
                <>
                    {/* Historical Fee Tracking */}
                    <div className="bg-white p-6 rounded-lg shadow-md">
                        <h3 className="text-lg font-semibold mb-4">Historical Fee Tracking</h3>
                        <div className="overflow-x-auto">
                            <table className="w-full border-collapse border border-gray-300">
                                <thead>
                                    <tr className="bg-gray-50">
                                        <th className="border border-gray-300 px-4 py-2 text-left">Year</th>
                                        <th className="border border-gray-300 px-4 py-2 text-right">Total Fees</th>
                                        <th className="border border-gray-300 px-4 py-2 text-right">Audit</th>
                                        <th className="border border-gray-300 px-4 py-2 text-right">Admin</th>
                                        <th className="border border-gray-300 px-4 py-2 text-right">Accounting</th>
                                        <th className="border border-gray-300 px-4 py-2 text-right">Change</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {historicalFees.map((year, index) => {
                                        const prevYear = index > 0 ? historicalFees[index - 1] : null;
                                        const change = prevYear ? year.totalFees - prevYear.totalFees : 0;
                                        return (
                                            <tr key={year.year}>
                                                <td className="border border-gray-300 px-4 py-2 font-medium">{year.year}</td>
                                                <td className="border border-gray-300 px-4 py-2 text-right">${year.totalFees.toLocaleString()}</td>
                                                <td className="border border-gray-300 px-4 py-2 text-right">${year.auditFees.toLocaleString()}</td>
                                                <td className="border border-gray-300 px-4 py-2 text-right">${year.adminFees.toLocaleString()}</td>
                                                <td className="border border-gray-300 px-4 py-2 text-right">${year.accountingFees.toLocaleString()}</td>
                                                <td className={`border border-gray-300 px-4 py-2 text-right ${
                                                    change > 0 ? 'text-red-600' : change < 0 ? 'text-green-600' : 'text-gray-600'
                                                }`}>
                                                    {change > 0 ? '+' : ''}${change.toLocaleString()}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Fee Efficiency Metrics */}
                    <div className="bg-white p-6 rounded-lg shadow-md">
                        <h3 className="text-lg font-semibold mb-4">Fee Efficiency Metrics</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <div className="text-center p-4 bg-blue-50 rounded-lg">
                                <div className="text-2xl font-bold text-blue-600">
                                    {(currentFees.totalFees / currentFees.estimatedBalance * 10000).toFixed(1)}
                                </div>
                                <div className="text-sm text-blue-700">Basis Points</div>
                            </div>
                            <div className="text-center p-4 bg-green-50 rounded-lg">
                                <div className="text-2xl font-bold text-green-600">
                                    {((currentFees.totalFees / industryBenchmarks.industry.totalFees - 1) * 100).toFixed(1)}%
                                </div>
                                <div className="text-sm text-green-700">vs Industry Super</div>
                            </div>
                            <div className="text-center p-4 bg-purple-50 rounded-lg">
                                <div className="text-2xl font-bold text-purple-600">
                                    {((currentFees.totalFees / industryBenchmarks.retail.totalFees - 1) * 100).toFixed(1)}%
                                </div>
                                <div className="text-sm text-purple-700">vs Retail Super</div>
                            </div>
                            <div className="text-center p-4 bg-orange-50 rounded-lg">
                                <div className="text-2xl font-bold text-orange-600">
                                    {optimizationRecommendations.reduce((sum, rec) => sum + rec.savings, 0).toLocaleString()}
                                </div>
                                <div className="text-sm text-orange-700">Potential Savings</div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Action Items */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Recommended Actions</h3>
                <div className="space-y-3">
                    <div className="flex items-center p-3 bg-blue-50 rounded-lg">
                        <span className="mr-3">📊</span>
                        <span className="text-sm">Review audit quotes annually and consider competitive tendering</span>
                    </div>
                    <div className="flex items-center p-3 bg-green-50 rounded-lg">
                        <span className="mr-3">📈</span>
                        <span className="text-sm">Monitor fee trends and benchmark against industry standards</span>
                    </div>
                    <div className="flex items-center p-3 bg-purple-50 rounded-lg">
                        <span className="mr-3">🔍</span>
                        <span className="text-sm">Consider automation tools to reduce accounting costs</span>
                    </div>
                    <div className="flex items-center p-3 bg-yellow-50 rounded-lg">
                        <span className="mr-3">⚖️</span>
                        <span className="text-sm">Evaluate SMSF cost-effectiveness as balance grows</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SMSFEnhancedFeeAnalysis;

